import { Button, Descriptions } from "antd";
import { useState, type ReactNode } from "react";
import { DetailHeader as ZhihaoDetailHeader } from "zhihao-ui-react";
export interface InfoItem {
    label: ReactNode;
    value?: ReactNode;
}
/** 详情页头兼容入口；外层复用 zhihao-ui-react，信息项结构严格对齐旧 Vue 公共组件。 */
export function DetailHeader({
    title,
    leftInfos = [],
    rightInfos = [],
    state,
    extra,
    extraInfo,
    menu,
    tabs,
    quoteCount
}: {
    title: ReactNode;
    leftInfos?: InfoItem[];
    rightInfos?: InfoItem[];
    state?: { text?: string; color?: string };
    extra?: ReactNode;
    extraInfo?: ReactNode;
    menu?: ReactNode;
    tabs?: ReactNode;
    quoteCount?: { history?: boolean; value?: number | null };
}) {
    /** 统一补齐旧版空值占位，并提供稳定 key 给组件库。 */
    const normalizeInfos = (items: InfoItem[]) => items.map((item, index) => ({ key: index, label: item.label, value: item.value ?? "--" }));
    /** 旧 Vue 的 label 与 value 之间只有 4px 间距，不会自动追加中文冒号。 */
    const renderInfo = (item: ReturnType<typeof normalizeInfos>[number]) => (
        <div className="zh-detail-header__info" key={item.key}>
            <span className="zh-detail-header__info-label">{item.label}</span>
            <span className="zh-detail-header__info-value">{item.value}</span>
        </div>
    );
    const normalizedLeftInfos = normalizeInfos(leftInfos);
    /** 组件库会给 leftInfos 自动补“：”，因此改走标题节点复刻旧 Vue 的无冒号结构。 */
    const titleNode = <>
        <span className="detail-header__title-text">{title}</span>
        {extraInfo}
        {normalizedLeftInfos.length ? (
            <div className="zh-detail-header__left-infos">{normalizedLeftInfos.map(renderInfo)}</div>
        ) : null}
    </>;
    /** 自定义状态色需要走右侧插槽，右侧信息同样复用旧 Vue 的无冒号结构。 */
    const rightInfosNode = <>
        {extra}
        {rightInfos.length ? <div className="zh-detail-header__right-infos">{normalizeInfos(rightInfos).map(renderInfo)}</div> : null}
        {state?.text ? <span className="detail-header__state" style={{ color: state.color }}>{state.text}</span> : null}
        {quoteCount?.value ? <>
            <span className="detail-header__gap" />
            <span className={quoteCount.history ? "detail-header__quote-history" : "detail-header__quote-current"}>
                {quoteCount.history ? "历史" : "当前"}{quoteCount.value}人报价
            </span>
        </> : null}
    </>;
    return (
        <ZhihaoDetailHeader
            className="detail-header common-card"
            title={titleNode}
            rightInfosNode={rightInfosNode}
            menu={menu}
        >
            {tabs}
        </ZhihaoDetailHeader>
    );
}
/** 可折叠信息卡片，对齐旧版 defaultCollapse 行为。 */
export function InfoCard({
    title,
    tag,
    tip,
    collapsible,
    defaultCollapsed = false,
    children
}: {
    title: ReactNode;
    tag?: ReactNode;
    tip?: ReactNode;
    collapsible?: boolean;
    defaultCollapsed?: boolean;
    children?: ReactNode;
}) {
    const [collapsed, setCollapsed] = useState(defaultCollapsed);
    return (
        <section className="info-card common-card">
            <header className="info-card__head">
                <div className={`info-card__title${collapsed ? "" : " info-card__title--open"}`}>
                    <span className="info-card__decorate" />
                    <strong>{title}</strong>
                    {tag ? <span className="info-card__tag">{tag}</span> : null}
                    {tip ? <small>{tip}</small> : null}
                </div>
                {collapsible ? (
                    <button
                        aria-label={collapsed ? "展开" : "收起"}
                        className="info-card__collapse"
                        onClick={() => setCollapsed(value => !value)}
                        type="button"
                    >
                        <i className={`iconfont ${collapsed ? "icon-down" : "icon-up"}`} />
                    </button>
                ) : null}
            </header>
            {collapsed ? null : children}
        </section>
    );
}
/** 栅格详情信息；top 模式复刻旧 detailJson 的响应式纵向标签布局。 */
export function BaseInfo({
    items,
    columns = 5,
    labelPlacement = "left"
}: {
    items: InfoItem[];
    columns?: number;
    labelPlacement?: "left" | "top";
}) {
    if (labelPlacement === "top") {
        return (
            <div className="base-info base-info--top">
                {items.map((item, index) => (
                    <div className="base-info__item" key={index}>
                        <div className="base-info__label">{item.label}</div>
                        <div className="base-info__value">{item.value ?? "--"}</div>
                    </div>
                ))}
            </div>
        );
    }
    return (
        <Descriptions
            column={columns}
            items={items.map((item, index) => ({ key: index, label: item.label, children: item.value ?? "--" }))}
        />
    );
}
export interface DetailAction<T> {
    label: string;
    visible?: (entity: T) => boolean;
    disabled?: boolean;
    action: (entity: T) => void | Promise<void>;
}
/** 详情操作组逐按钮隔离 loading，避免异步操作重复提交。 */
export function DetailButtons<T>({ actions, entity }: { actions: DetailAction<T>[]; entity: T }) {
    const [loadingIndex, setLoadingIndex] = useState<number>();
    const run = async (action: DetailAction<T>, index: number) => {
        setLoadingIndex(index);
        try {
            await action.action(entity);
        } finally {
            setLoadingIndex(undefined);
        }
    };
    return (
        <div className="detail-buttons">
            {actions.map((action, index) =>
                action.visible?.(entity) === false ? null : (
                    <Button
                        key={action.label}
                        type="primary"
                        disabled={action.disabled || loadingIndex != null}
                        loading={loadingIndex === index}
                        onClick={() => void run(action, index)}
                    >
                        {action.label}
                    </Button>
                )
            )}
        </div>
    );
}
